import React, { useMemo } from 'react';

import EntityDropDownFilterContainerComponent from 'components/entityViewer/entitiesDropDownFilter/EntityDropDownFilterContainer';
import { useFacilityOptions } from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';
import {
    ALL_FC_TRANSFER_ITEMS_FILTER_QUERY_KEYS,
    AllFCTransferItemsFilterConfigArray,
} from 'components/warehouse/components/fcTransfers/allTransfers/allFCTransferItemsFilterConfig';
import { useFCTransferStatusOptions } from 'components/warehouse/components/fcTransfers/queries/FCTransferQueries';
import { useCompanySourceOptionsQuery } from 'shared/hooks/inventory/useCompanySourceOptionsQuery';

const EMPTY_INITIAL_FILTER_VALUES = Object.freeze({});

/**
 * Merch all-FC transfer **items** filter bar.
 *
 * @param {Object} props
 * @param {Object} [props.initialValues={}]
 * @param {(filters: Object) => void} [props.onChange]
 * @param {string} [props.className]
 */
export function AllFCTransfersFilters({ className = '', initialValues = EMPTY_INITIAL_FILTER_VALUES, onChange }) {
    const { data: facilityOptions = [], isLoading: isLoadingFacilities } = useFacilityOptions();
    const { data: statusOptions = [], isLoading: isLoadingStatuses } = useFCTransferStatusOptions();
    const { data: companySources = [], isLoading: isLoadingCompanySources } = useCompanySourceOptionsQuery();

    const companySourceOptions = useMemo(
        () =>
            companySources.map((source) => ({
                value: source.id,
                label: source.displayName,
            })),
        [companySources],
    );

    const effectiveFilterConfigs = useMemo(
        () =>
            AllFCTransferItemsFilterConfigArray.map((cfg) => {
                if (cfg.remoteOptions?.queryId === ALL_FC_TRANSFER_ITEMS_FILTER_QUERY_KEYS.FACILITIES) {
                    return {
                        ...cfg,
                        options: facilityOptions,
                        menuProps: {
                            ...cfg.menuProps,
                            isLoading: isLoadingFacilities,
                        },
                    };
                }
                if (cfg.remoteOptions?.queryId === ALL_FC_TRANSFER_ITEMS_FILTER_QUERY_KEYS.TRANSFER_STATUSES) {
                    return {
                        ...cfg,
                        options: statusOptions,
                        menuProps: {
                            ...cfg.menuProps,
                            isLoading: isLoadingStatuses,
                        },
                    };
                }
                if (cfg.remoteOptions?.queryId === ALL_FC_TRANSFER_ITEMS_FILTER_QUERY_KEYS.COMPANY_SOURCES) {
                    return {
                        ...cfg,
                        options: companySourceOptions,
                        menuProps: {
                            ...cfg.menuProps,
                            isLoading: isLoadingCompanySources,
                        },
                    };
                }
                return cfg;
            }),
        [
            companySourceOptions,
            facilityOptions,
            isLoadingCompanySources,
            isLoadingFacilities,
            isLoadingStatuses,
            statusOptions,
        ],
    );

    return (
        <div className={`flex flex-col gap-3 ${className}`.trim()}>
            <EntityDropDownFilterContainerComponent
                title="All FC Transfer Item Filters"
                filterConfigs={effectiveFilterConfigs}
                initialValues={initialValues}
                onChange={onChange}
                autoApply
            />
        </div>
    );
}

AllFCTransfersFilters.displayName = 'AllFCTransfersFilters';
